import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { GetPublishedJobs } from "../Services/CAllJobsService";
import Skeleton, { SkeletonTheme } from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";

import { Funnel, MoreVertical, Eye, UserPlus } from "lucide-react";

import ColumnFilterModal from "../../../Employee/CommonModals/ColumnFilterModal";
import Pagination from "../../../Employee/CommonModals/Pagination";
import NormalAlert from "../../../Alerts/NormalAlert";

const AllJobs = () => {
  const navigate = useNavigate();

  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });

  // DATA
  const [jobs, setJobs] = useState([]);

  // UI
  const [loading, setLoading] = useState(true);
  const [alert, setAlert] = useState(null);
  const [openMenu, setOpenMenu] = useState(null);

  // SEARCH
  const [searchTerm, setSearchTerm] = useState("");

  // FILTER
  const [activeFilter, setActiveFilter] = useState(null);
  const [filterSearch, setFilterSearch] = useState("");

  const [filters, setFilters] = useState({
    jobTitle: [],
  });

  // PAGINATION
  const [currentPage, setCurrentPage] = useState(1);
  const [jobsPerPage, setJobsPerPage] = useState(10);

  // FETCH JOBS
  useEffect(() => {
    const fetchJobs = async () => {
      try {
        const res = await GetPublishedJobs();

        const response = res?.data;

        //  If API returns no jobs
        if (response?.status === "Success" && response?.publishedJobs == null) {
          setJobs([]);
          setLoading(false);
          return;
        }

        const publishedJobs = response?.publishedJobs;

        if (Array.isArray(publishedJobs)) {
          setJobs(publishedJobs);
        } else {
          setJobs([]);
        }
      } catch (err) {
        if (err?.response?.data?.message !== "No jobs published.") {
          setAlert({
            type: "fail",
            message:
              err?.response?.data?.message || "Failed to fetch published jobs",
          });
        }
      } finally {
        setLoading(false);
      }
    };

    fetchJobs();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest(".action-menu")) {
        setOpenMenu(null);
      }
    };

    document.addEventListener("click", handleClickOutside);

    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, []);

  // FILTER OPTIONS
  const filterOptions = {
    jobTitle: [...new Set(jobs.map((j) => j.jobTitle))],
  };

  // FILTERED JOBS
  const filteredJobs = jobs.filter((job) => {
    const selectedTitles = filters.jobTitle;

    const matchesFilter =
      selectedTitles.length === 0 || selectedTitles.includes(job.jobTitle);

    const matchesSearch = [
      job.jobTitle,
      job.department,
      job.location,
      job.grade,
      job.function,
    ].some((field) => field?.toLowerCase().includes(searchTerm.toLowerCase()));

    return matchesFilter && matchesSearch;
  });

  // PAGINATION
  const indexOfLast = currentPage * jobsPerPage;
  const indexOfFirst = indexOfLast - jobsPerPage;

  const currentJobs = filteredJobs.slice(indexOfFirst, indexOfLast);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filters]);

  // ACTIONS
  const handleViewDetails = (jobUID) => {
    navigate(`/Consultant/Dashboard/Consultant-Job-Details/${jobUID}`);
  };

  const handleSubmitCandidate = (jobUID) => {
    navigate(`/Consultant/Dashboard/Send-Candidate/${jobUID}`);
  };

  return (
    <>
      <div className="w-full py-8 mt-12">
        <div className="max-w-[1240px] mx-auto">
          <div className="bg-white rounded-2xl px-3 sm:px-4 md:px-6 py-4 md:py-6 shadow-sm">
            {/* HEADER */}
            <div className="bg-master-header text-white rounded-xl px-6 py-3 mb-6">
              <h3 className="font-bold text-lg">All Jobs</h3>
            </div>

            {/* SEARCH */}
            <div className="mb-6 flex justify-between items-center flex-wrap gap-4">
              <input
                type="text"
                placeholder="Search by title, department..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="border border-gray-300 rounded-full px-5 py-2 w-full max-w-sm focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>

            {/* TABLE */}
            <div className="border rounded-lg overflow-x-auto overflow-y-visible relative">
              <table className="w-full min-w-[900px] text-center relative">
                <thead className="bg-[#f5f8ff] text-sm uppercase">
                  <tr>
                    <th className="py-2 md:py-3 text-xs md:text-sm">#</th>

                    <th className="py-2 md:py-3 text-xs md:text-sm">
                      <div className="flex justify-center items-center gap-2">
                        Role Code
                      </div>
                    </th>

                    <th className="py-2 md:py-3 text-xs md:text-sm">
                      <div className="flex justify-center items-center gap-2">
                        Title
                        <Funnel
                          size={14}
                          onClick={() => setActiveFilter("jobTitle")}
                          className="cursor-pointer text-primary"
                        />
                      </div>
                    </th>

                    <th className="py-2 md:py-3 text-xs md:text-sm">
                      Department
                    </th>
                    <th className="py-2 md:py-3 text-xs md:text-sm">
                      Location
                    </th>
                    <th className="py-2 md:py-3 text-xs md:text-sm">Grade</th>
                    <th className="py-2 md:py-3 text-xs md:text-sm">
                      Submitted Candidates
                    </th>
                    <th className="py-2 md:py-3 text-xs md:text-sm">
                      Function
                    </th>
                    <th className="py-2 md:py-3 text-xs md:text-sm">Action</th>
                  </tr>
                </thead>

                <tbody>
                  {loading ? (
                    <SkeletonTheme baseColor="#e5e7eb" highlightColor="#f3f4f6">
                      {[...Array(5)].map((_, rowIdx) => (
                        <tr key={rowIdx} className="border-t">
                          {[...Array(8)].map((__, colIdx) => (
                            <td key={colIdx} className="py-4 px-3">
                              <Skeleton height={20} />
                            </td>
                          ))}
                        </tr>
                      ))}
                    </SkeletonTheme>
                  ) : currentJobs.length ? (
                    currentJobs.map((job, idx) => (
                      <tr key={job.jobUID || idx} className="border-t relative">
                        <td className="py-2 md:py-3 px-2 md:px-3 text-xs md:text-[15px]">
                          {indexOfFirst + idx + 1}
                        </td>

                        <td className="py-2 md:py-3 px-2 md:px-3 text-xs md:text-[15px] font-semibold">
                          {job.roleCode || "Role Code Missing"}
                        </td>

                        <td
                          className="text-blue-600 cursor-pointer py-2 md:py-3 px-2 md:px-3 text-xs md:text-[15px] font-semibold"
                          onClick={() => handleViewDetails(job.jobUID)}
                        >
                          {job.jobTitle || "Untitled Job"}
                        </td>

                        <td className="py-2 md:py-3 px-2 md:px-3 text-xs md:text-[15px]">
                          {job.department || "N/A"}
                        </td>

                        <td className="py-2 md:py-3 px-2 md:px-3 text-xs md:text-[15px]">
                          {job.location || "N/A"}
                        </td>

                        <td className="py-2 md:py-3 px-2 md:px-3 text-xs md:text-[15px]">
                          {job.grade || "N/A"}
                        </td>

                        <td className="py-2 md:py-3 px-2 md:px-3 text-xs md:text-[15px]">
                          <Link
                            to={`/Consultant/Dashboard/Sent-Candidate-For-Job/${job.jobUID}`}
                            className="text-blue-600 font-semibold"
                          >
                            View ({job.candidateSent || 0})
                          </Link>
                        </td>

                        <td className="py-2 md:py-3 px-2 md:px-3 text-xs md:text-[15px]">
                          {job.function || "N/A"}
                        </td>

                        <td className="py-2 md:py-3 px-2 md:px-3 text-xs md:text-[15px]">
                          <div className="relative inline-block action-menu overflow-visible">
                            <button
                              onClick={(e) => {
                                const rect =
                                  e.currentTarget.getBoundingClientRect();

                                setMenuPosition({
                                  top: rect.bottom + window.scrollY,
                                  left: Math.max(10, rect.right - 180),
                                });

                                setOpenMenu(
                                  openMenu === job.jobUID ? null : job.jobUID,
                                );
                              }}
                              className="p-1 hover:bg-gray-100 rounded-full"
                            >
                              <MoreVertical size={20} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={window.innerWidth < 768 ? 4 : 8}
                       className="py-6 text-red-500 text-center">
                        No Jobs Found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>

              {openMenu && (
                <div
                  style={{
                    position: "fixed",
                    top: menuPosition.top,
                    left: menuPosition.left,
                    zIndex: 9999,
                  }}
                  className="w-40 sm:w-44 bg-white border rounded-lg shadow-lg"
                >
                  <button
                    className="w-full text-left px-4 py-2 hover:bg-gray-100 text-sm flex items-center gap-2 whitespace-nowrap"
                    onClick={() => {
                      handleViewDetails(openMenu);
                      setOpenMenu(null);
                    }}
                  >
                    <Eye size={16} className="shrink-0" />
                    View Details
                  </button>

                  <button
                    className="w-full text-left px-4 py-2 hover:bg-gray-100 text-sm flex items-center gap-2 whitespace-nowrap"
                    onClick={() => {
                      handleSubmitCandidate(openMenu);
                      setOpenMenu(null);
                    }}
                  >
                    <UserPlus size={16} className="shrink-0" />
                    Submit Candidate
                  </button>
                </div>
              )}
            </div>

            {/* PAGINATION */}
            <Pagination
              totalItems={filteredJobs.length}
              itemsPerPage={jobsPerPage}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              setItemsPerPage={setJobsPerPage}
            />
          </div>
        </div>
      </div>

      {/* FILTER MODAL */}
      <ColumnFilterModal
        show={!!activeFilter}
        onHide={() => setActiveFilter(null)}
        title={activeFilter}
        items={filterOptions[activeFilter] || []}
        filteredItems={(filterOptions[activeFilter] || []).filter((item) =>
          item?.toLowerCase().includes(filterSearch.toLowerCase()),
        )}
        selectedItems={filters[activeFilter] || []}
        setSelectedItems={(values) =>
          setFilters((prev) => ({
            ...prev,
            [activeFilter]: values,
          }))
        }
        searchText={filterSearch}
        setSearchText={setFilterSearch}
      />

      {alert && (
        <NormalAlert
          type={alert.type}
          message={alert.message}
          onClose={() => setAlert(null)}
        />
      )}
    </>
  );
};

export default AllJobs;
